import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, AlertCircle } from 'lucide-react'
import { getFunnelConnector, getSourceTables, saveFunnelConfig, updateFunnelConnector, listFunnelConnectors } from '../api/funnel'
import { getDatasets } from '../api/storage'
import { Steps, StepTables, StepColumns, funnelTableToConfig } from './BrainerFunnelWizard'
import type { TableConfig } from './BrainerFunnelWizard'
import type { FunnelConnector } from '../types'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import { Combobox } from '../components/ui/combobox'

type EditStep = 0 | 1 | 2

// ── Step 0: Edit connection details ───────────────────────────────────────────

function StepEditConnect({
  connector,
  onNext,
  onBack,
}: {
  connector: FunnelConnector
  onNext: () => void
  onBack: () => void
}) {
  const qc = useQueryClient()
  const isSftp = connector.connectorType === 'sftp'
  const [form, setForm] = useState({
    title:        connector.title,
    host:         connector.host,
    port:         String(connector.port),
    databaseName: connector.databaseName,
    username:     connector.username,
    password:     '',
    storageDatasetId: connector.storageDatasetId ?? '',
    sftpConnectorId: connector.sftpConnectorId ?? '',
    sftpRemotePath: connector.sftpRemotePath ?? '',
    sftpFileTypeFilter: connector.sftpFileTypeFilter ?? '',
    sftpAuthType: connector.sftpAuthType ?? 'password',
    privateKey: '',
  })
  const [error, setError] = useState('')
  const { data: datasets = [] } = useQuery({ queryKey: ['storage-datasets'], queryFn: () => getDatasets().then(r => r.data) })
  const { data: connectors = [] } = useQuery({
    queryKey: ['funnel-connectors'], queryFn: () => listFunnelConnectors().then(r => r.data), enabled: !isSftp,
  })
  const sftpConnectors = connectors.filter(c => c.connectorType === 'sftp' && c.id !== connector.id)

  const mut = useMutation({
    mutationFn: () => updateFunnelConnector(connector.id, {
      title: form.title, host: form.host, port: Number(form.port), username: form.username,
      storageDatasetId: form.storageDatasetId || null,
      ...(isSftp
        ? {
            sftpRemotePath: form.sftpRemotePath || null,
            sftpFileTypeFilter: form.sftpFileTypeFilter || null,
            sftpAuthType: form.sftpAuthType,
            ...(form.sftpAuthType === 'password' && form.password ? { password: form.password } : {}),
            ...(form.sftpAuthType === 'key' && form.privateKey ? { privateKey: form.privateKey } : {}),
          }
        : {
            databaseName: form.databaseName,
            sftpConnectorId: form.sftpConnectorId || null,
            ...(form.password ? { password: form.password } : {}),
          }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['funnel-connector',      connector.id] })
      qc.invalidateQueries({ queryKey: ['funnel-source-tables',  connector.id] })
      if (isSftp) {
        onBack() // no Tables/Columns steps for SFTP connectors — go straight back to the detail page
      } else {
        onNext()
      }
    },
    onError: (e: any) => setError(e.response?.data?.error ?? e.message ?? 'Update failed'),
  })

  const canSubmit = isSftp
    ? !!(form.title && form.host && form.username && form.sftpRemotePath)
    : !!(form.title && form.host && form.databaseName && form.username)

  return (
    <div className="space-y-3">
      <Field label="Display name">
        <input
          type="text"
          value={form.title}
          onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          className="field"
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Host">
            <input
              type="text"
              value={form.host}
              onChange={e => setForm(p => ({ ...p, host: e.target.value }))}
              className="field"
            />
          </Field>
        </div>
        <Field label="Port">
          <input
            type="number"
            value={form.port}
            onChange={e => setForm(p => ({ ...p, port: e.target.value }))}
            className="field"
          />
        </Field>
      </div>
      {!isSftp && (
        <Field label="Database name">
          <input
            type="text"
            value={form.databaseName}
            onChange={e => setForm(p => ({ ...p, databaseName: e.target.value }))}
            className="field"
          />
        </Field>
      )}
      <Field label="Username">
        <input
          type="text"
          value={form.username}
          onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
          className="field"
        />
      </Field>

      {isSftp ? (
        <>
          <Field label="Authentication">
            <Select
              value={form.sftpAuthType}
              onValueChange={v => setForm(p => ({ ...p, sftpAuthType: v as 'password' | 'key' }))}
            >
              <SelectItem value="password">Password</SelectItem>
              <SelectItem value="key">SSH private key</SelectItem>
            </Select>
          </Field>
          {form.sftpAuthType === 'password' ? (
            <Field label="Password">
              <input
                type="password"
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Leave blank to keep current"
                className="field"
              />
            </Field>
          ) : (
            <Field label="Private key">
              <textarea
                value={form.privateKey}
                onChange={e => setForm(p => ({ ...p, privateKey: e.target.value }))}
                placeholder="Leave blank to keep current"
                rows={4}
                className="field font-mono text-xs"
              />
            </Field>
          )}
          <Field label="Remote path">
            <input
              type="text"
              value={form.sftpRemotePath}
              onChange={e => setForm(p => ({ ...p, sftpRemotePath: e.target.value }))}
              className="field"
            />
          </Field>
          <Field label="File type filter (optional)">
            <input
              type="text"
              value={form.sftpFileTypeFilter}
              onChange={e => setForm(p => ({ ...p, sftpFileTypeFilter: e.target.value }))}
              placeholder="pdf, png, txt"
              className="field"
            />
          </Field>
          <Field label="Destination dataset">
            <Combobox
              value={form.storageDatasetId}
              onValueChange={v => setForm(p => ({ ...p, storageDatasetId: v }))}
              placeholder="Select a dataset..."
              options={datasets.map(d => ({ value: d.id, label: d.title }))}
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Password">
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              placeholder="Leave blank to keep current"
              className="field"
            />
          </Field>
          <Field label="Storage dataset (for _document columns)">
            <Combobox
              value={form.storageDatasetId}
              onValueChange={v => setForm(p => ({ ...p, storageDatasetId: v }))}
              placeholder="None — leave unset unless importing document columns"
              options={[
                { value: '', label: 'None — leave unset unless importing document columns' },
                ...datasets.map(d => ({ value: d.id, label: d.title })),
              ]}
            />
          </Field>
          <Field label="SFTP source (for _documentPath columns)">
            <Combobox
              value={form.sftpConnectorId}
              onValueChange={v => setForm(p => ({ ...p, sftpConnectorId: v }))}
              placeholder="None — leave unset unless importing file-path columns"
              options={[
                { value: '', label: 'None — leave unset unless importing file-path columns' },
                ...sftpConnectors.map(c => ({ value: c.id, label: c.title })),
              ]}
            />
          </Field>
        </>
      )}

      {error && (
        <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded p-2.5">
          <AlertCircle size={13} className="shrink-0 mt-px" /> {error}
        </div>
      )}

      <div className="flex justify-between pt-1">
        <Button variant="secondary" onClick={onBack}>Cancel</Button>
        <Button
          onClick={() => { setError(''); mut.mutate() }}
          disabled={mut.isPending || !canSubmit}
          loading={mut.isPending}
        >
          {isSftp ? 'Save' : 'Save & continue'}
          {!isSftp && <ChevronRight size={14} />}
        </Button>
      </div>
    </div>
  )
}

// ── Main edit page ─────────────────────────────────────────────────────────────

export function BrainerFunnelEdit() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc       = useQueryClient()

  const [step, setStep]       = useState<EditStep>(0)
  const [tables, setTables]   = useState<TableConfig[]>([])
  const [initialized, setInitialized] = useState(false)
  const [saving, setSaving]   = useState(false)
  const [saveError, setSaveError] = useState('')

  const { data: connectorData, isLoading: connLoading } = useQuery({
    queryKey: ['funnel-connector', id],
    queryFn:  () => getFunnelConnector(id!).then(r => r.data),
    enabled:  !!id,
  })

  const isSftp = connectorData?.connectorType === 'sftp'

  const { data: sourceTablesRaw = [], isLoading: srcLoading, isError: srcError } = useQuery({
    queryKey: ['funnel-source-tables', id],
    queryFn:  () => getSourceTables(id!).then(r => r.data),
    enabled:  !!id && !isSftp,
    retry: 1,
  })

  // Initialise tables from existing config once (do not overwrite on refetch)
  useMemo(() => {
    if (!connectorData || initialized) return
    setTables((connectorData.tables ?? []).map(funnelTableToConfig))
    setInitialized(true)
  }, [connectorData, initialized])

  // Merge source tables with already-configured ones so editing works even if
  // the source DB is temporarily unavailable.
  const sourceTables = useMemo(() => {
    const all = new Set([
      ...(sourceTablesRaw as string[]),
      ...tables.map(t => t.tableName),
    ])
    return [...all].sort()
  }, [sourceTablesRaw, tables])

  const handleSave = async () => {
    if (!connectorData) return
    setSaving(true); setSaveError('')
    try {
      await saveFunnelConfig(id!, {
        tables: tables.map(t => ({
          tableName: t.tableName,
          cupName:   t.cupName || null,
          tableType: t.tableType,
          idColumn:  t.idColumn,
          isAutoIncluded: false,
          ...(t.tableType === 'data' ? {
            columns: t.columns.map(c => ({
              columnName:             c.columnName,
              traitName:              c.traitName || c.columnName.replace(/\s+/g, '_'),
              isRelation:             c.isRelation,
              isImported:             c.isImported,
              relationTargetTable:    c.isRelation ? c.relationTargetTable    || null : null,
              relationTargetIdColumn: c.isRelation ? c.relationTargetIdColumn || null : null,
              ingredientId:           c.isRelation ? null : c.ingredientId || null,
            })),
          } : {
            links: [
              { linkedTableName: t.link1TableName, linkedIdColumn: t.link1IdColumn, position: 1 as const },
              { linkedTableName: t.link2TableName, linkedIdColumn: t.link2IdColumn, position: 2 as const },
            ],
            label: t.label || null,
          }),
        })),
      })
      qc.invalidateQueries({ queryKey: ['funnel-connector',  id] })
      qc.invalidateQueries({ queryKey: ['funnel-connectors'] })
      navigate(`/brainer/funnels/${id}`)
    } catch (e: any) {
      setSaveError(e.response?.data?.error ?? 'Save failed')
      setSaving(false)
    }
  }

  if (connLoading || !initialized) {
    return <div className="py-16 text-center text-sm text-ink-dim">Loading…</div>
  }
  if (!connectorData) {
    return <div className="py-16 text-center text-sm text-red-500">Connector not found</div>
  }

  const connector = connectorData

  return (
    <div className="max-w-2xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-5 text-sm">
        <button onClick={() => navigate('/brainer/funnels')}
                className="text-ink-dim hover:text-ink transition-colors">
          Connectors
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <button onClick={() => navigate(`/brainer/funnels/${id}`)}
                className="text-ink-dim hover:text-ink transition-colors">
          {connector.title}
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <span className="text-ink font-medium">Configure</span>
      </div>

      <h1 className="text-xl font-semibold text-ink mb-4">Configure {connector.title}</h1>

      <Steps current={step} />

      {!isSftp && step >= 1 && srcError && (
        <div className="flex items-start gap-2 text-amber-700 text-xs bg-amber-50 border border-amber-100 rounded p-2.5 mb-4">
          <AlertCircle size={13} className="shrink-0 mt-px" />
          Could not reach the source database — existing tables are shown, but adding new ones requires connectivity.
        </div>
      )}

      {saveError && (
        <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded p-2.5 mb-4">
          <AlertCircle size={13} className="shrink-0 mt-px" /> {saveError}
        </div>
      )}

      {step === 0 && (
        <StepEditConnect
          connector={connector}
          onNext={() => setStep(1)}
          onBack={() => navigate(`/brainer/funnels/${id}`)}
        />
      )}

      {step === 1 && (
        <StepTables
          connector={connector}
          sourceTables={srcLoading ? tables.map(t => t.tableName) : sourceTables}
          tables={tables}
          onChange={setTables}
          onNext={() => setStep(2)}
          onBack={() => setStep(0)}
        />
      )}

      {step === 2 && (
        <StepColumns
          connector={connector}
          tables={tables}
          onChange={setTables}
          onSave={handleSave}
          onBack={() => setStep(1)}
          saving={saving}
        />
      )}
    </div>
  )
}
